// @flow
import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { getTreeNav, productSearch } from '../actions/pages/products';
import { setProductsOptions } from '../actions/pages/cmsProductsOptions';
import { addToCart } from '../actions/cart/addToCart';
import LoadingIndicator from '../components/LoadingIndicator';
import ProductSearch from './ProductSearch';
import ProductCard from '../components/ProductCard';
import RoyMorganContact from '../components/RoyMorganContact';
import { Card, Pagination, Grid, Container } from 'semantic-ui-react';
import TreeNav from '../components/TreeNav';

const Products = (): React$Node => {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();

  const page = useSelector((state) => state.productList);
  const treenav = useSelector((state) => state.treenav);
  const isFetching = useSelector((state) => state.productList.isFetching);
  const totalPages = useSelector((state) => state.productList.totalPages);
  const currentPage = useSelector((state) => state.productList.currentPage);
  const totalItems = useSelector((state) => state.productList.totalItems);
  const searchedCategory = useSelector(
    (state) => state.productList.searchedCategory,
  );
  const searchedKeyword = useSelector(
    (state) => state.productList.searchedKeyword,
  );
  const promoBanner = useSelector(
    (state) => state.cmsProductsOptions?.productListingPromoBanner?.url,
  );
  const cartItems = useSelector((state) => state.cart?.cartItems);

  const urlParams = new URLSearchParams(location.search);
  const reportType = urlParams.has('reportTypeId')
    ? Number(urlParams.get('reportTypeId'))
    : undefined;

  React.useEffect(() => {
    dispatch(getTreeNav());
    dispatch(setProductsOptions());
  }, [dispatch]);

  const handlePaginationChange = React.useCallback(
    (e, { activePage }) => {
      dispatch(
        productSearch(
          searchedCategory,
          searchedKeyword,
          reportType,
          activePage,
        ),
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [dispatch, searchedCategory, searchedKeyword, reportType],
  );

  const newProductSearch = React.useCallback(
    (searchedCategory, searchedKeyword, reportTypeId) => {
      dispatch(
        productSearch(searchedCategory, searchedKeyword, reportTypeId, 1),
      );
    },
    [dispatch],
  );

  return (
    <Container className="products-listing">
      <Grid columns={12} stackable={true}>
        <Grid.Row>
          <Grid.Column width={3}>
            {treenav && treenav.treenav && (
              <TreeNav
                treeNav={treenav.treenav}
                treeflat={treenav.treeflat}
                match={{ params }}
                location={location}
                reportTypeId={reportType}
                productSearch={newProductSearch}
              />
            )}
          </Grid.Column>
          <Grid.Column width={9}>
            <ProductSearch
              totalProducts={totalItems}
              page={currentPage}
              reportTypeId={reportType}
            />
            {isFetching && <LoadingIndicator />}

            {!isFetching && totalItems === 0 && (
              <div className="no-results-content">
                <p>
                  Sorry, no results found. We may have the data needed for the
                  profile or report that you are looking for.
                </p>
                <p>
                  Contact us at Roy Morgan Online Store on{' '}
                  <RoyMorganContact.PhoneLink /> or email any queries to{' '}
                  <RoyMorganContact.EmailLink />
                </p>
              </div>
            )}

            {!isFetching && totalPages > 0 && (
              <div className="product-listing__pagination-wrapper product-listing__pagination-wrapper--top">
                <Pagination
                  activePage={currentPage}
                  onPageChange={handlePaginationChange}
                  totalPages={totalPages}
                  ellipsisItem={null}
                  boundaryRange={0}
                  siblingRange={3}
                />
              </div>
            )}
            {!isFetching && (
              <Card.Group itemsPerRow={2} className="search-results">
                {page?.products?.map((product, i) => {
                  const itemExistsInCart = cartItems?.some(
                    (el) => el.id === product?.id,
                  );
                  return (
                    <ProductCard
                      key={i}
                      product={product}
                      itemExistsInCart={itemExistsInCart}
                      addToCart={(payload) => dispatch(addToCart(payload))}
                    />
                  );
                })}
              </Card.Group>
            )}
            {!isFetching && totalPages > 0 && (
              <div className="product-listing__pagination-wrapper">
                <Pagination
                  activePage={currentPage}
                  onPageChange={handlePaginationChange}
                  totalPages={totalPages}
                  ellipsisItem={null}
                  boundaryRange={0}
                  siblingRange={3}
                />
              </div>
            )}
          </Grid.Column>
        </Grid.Row>
        {promoBanner && (
          <img
            className="ui fluid image"
            alt="Promo Banner"
            src={promoBanner}
          />
        )}
      </Grid>
    </Container>
  );
};

export default Products;
